import {
  abrirContaFormSchema,
  loginEmailPasswordFormSchema,
  loginMagicLinkFormSchema,
  mapAbrirContaFormToApiRequest,
  mapConfirmPasswordResetFormToApiRequest,
  mapLoginEmailPasswordFormToApiRequest,
  mapLoginMagicLinkFormToApiRequest,
  mapRecuperarSenhaFormToApiRequest,
  mapValidateOtpFormToApiRequest,
  recuperarSenhaFormSchema,
  validateOtpFormSchema,
  type AbrirContaForm,
  type LoginEmailPasswordForm,
  type LoginMagicLinkForm,
  type RecuperarSenhaForm,
  type ValidateOtpForm,
} from './auth.schema'
import type {
  LoginSuccessResponse,
  ApiErrorResponse,
  RefreshTokenResponse,
  RecuperarSenhaResponse,
  AbrirContaResponse,
  ValidationErrorResponse,
} from './auth.types'
import { auth } from '@imobdesk/contracts'
import { apiClientJsonParsed, parseWith } from '@/lib/api-client'
import { saveAccessToken } from '@/lib/token-storage'
import { API_BASE_URL } from '@/config/api'
import { normalizeApiError } from './auth.errors'

/**
 * Action para login com email e senha
 */
export async function loginEmailPassword(
  data: LoginEmailPasswordForm,
): Promise<LoginSuccessResponse> {
  try {
    loginEmailPasswordFormSchema.parse(data)
    const requestData = mapLoginEmailPasswordFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/login',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: unknown) {
    throw normalizeApiError(
      error,
      'Não foi possível concluir o login agora. Tente novamente em instantes.',
    )
  }
}

/**
 * Resposta de sucesso do login magic link
 */
export interface MagicLinkResponse {
  success: boolean
  message: string
}

/**
 * Action para login com magic link
 */
export async function loginMagicLink(
  data: LoginMagicLinkForm,
): Promise<MagicLinkResponse> {
  try {
    loginMagicLinkFormSchema.parse(data)
    const requestData = mapLoginMagicLinkFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/login-magic-link',
      auth.magicLinkResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao enviar magic link. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Action para recuperar senha
 */
export async function confirmPasswordReset(data: {
  token: string
  password: string
  passwordConfirmation: string
}): Promise<RecuperarSenhaResponse> {
  try {
    const requestData = mapConfirmPasswordResetFormToApiRequest(
      {
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      },
      data.token,
    )
    const response = await apiClientJsonParsed(
      '/api/v2/auth/reset-password',
      auth.recuperarSenhaResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )
    return response
  } catch (error: any) {
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    throw {
      message: 'Erro ao redefinir senha. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

export async function recuperarSenha(
  data: RecuperarSenhaForm,
): Promise<RecuperarSenhaResponse> {
  try {
    recuperarSenhaFormSchema.parse(data)
    const requestData = mapRecuperarSenhaFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/new-password',
      auth.recuperarSenhaResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao enviar email de recuperação. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Action para abrir conta (cadastro)
 */
export async function abrirConta(
  data: AbrirContaForm,
): Promise<AbrirContaResponse> {
  try {
    abrirContaFormSchema.parse(data)
    const requestData = mapAbrirContaFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/register',
      auth.abrirContaResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    const responseStatus =
      typeof error?.status === 'number' ? error.status : undefined

    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      // Verifica se é erro de validação (array de mensagens)
      if (Array.isArray(error.data.message)) {
        throw error.data as ValidationErrorResponse
      }
      if (error.data.statusCode === 404 || responseStatus === 404) {
        throw {
          message:
            'Cadastro indisponível no momento. Tente novamente em instantes.',
          error: 'Not Found',
          statusCode: 404,
        } as ApiErrorResponse
      }
      throw error.data as ApiErrorResponse
    }
    if (responseStatus === 404) {
      throw {
        message:
          'Cadastro indisponível no momento. Tente novamente em instantes.',
        error: 'Not Found',
        statusCode: 404,
      } as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao criar conta. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Action para validar magic link token (código OTP)
 */
export async function validateOtp(
  data: ValidateOtpForm,
): Promise<LoginSuccessResponse> {
  try {
    validateOtpFormSchema.parse(data)
    const requestData = mapValidateOtpFormToApiRequest(data)
    const response = await apiClientJsonParsed(
      '/api/v2/auth/check-magic-link',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    return response
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao validar código. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Action para validar magic link token apenas com o código (sem email)
 * Usado quando o usuário acessa pelo link direto
 */
export async function validateOtpByToken(
  token: string,
): Promise<LoginSuccessResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/auth/check-magic-link',
      auth.loginSuccessResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    )

    return response
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao validar código. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Resposta de sucesso do logout
 */
export interface LogoutResponse {
  success: boolean
  message: string
}

/**
 * Action para logout do usuário
 */
export async function logout(): Promise<LogoutResponse> {
  try {
    const response = await apiClientJsonParsed(
      '/api/v2/auth/logout',
      auth.logoutResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    )

    return response
  } catch (error: any) {
    // Mesmo se der erro na API, ainda limpa os tokens localmente
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao fazer logout',
      statusCode: 500,
    } as ApiErrorResponse
  }
}

/**
 * Action para renovar o access token usando o refresh token
 * Esta função faz uma chamada direta sem usar apiClientJson para evitar loop infinito
 */
export async function refreshToken(): Promise<RefreshTokenResponse> {
  try {
    // Faz chamada direta sem usar apiClient para evitar loop
    const url = API_BASE_URL
      ? `${API_BASE_URL}/api/v2/auth/refresh-token`
      : '/api/v2/auth/refresh-token'

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
    })

    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      throw new Error(`Resposta não é JSON: ${text}`)
    }

    const data = await response.json()

    if (!response.ok) {
      throw {
        status: response.status,
        statusText: response.statusText,
        data,
      }
    }

    // Valida a resposta no boundary com o contrato antes de confiar nela.
    const parsed = parseWith(auth.refreshTokenResponseSchema, data)
    saveAccessToken(parsed.accessToken)

    return parsed
  } catch (error: any) {
    // Se for erro da API, lança com a estrutura esperada
    if (error.data) {
      throw error.data as ApiErrorResponse
    }
    // Se for outro tipo de erro, lança genérico
    throw {
      message: 'Erro ao renovar token. Tente novamente.',
      error: 'Internal Server Error',
      statusCode: 500,
    } as ApiErrorResponse
  }
}
